import Copy16 from '@carbon/icons-react/es/copy/16';
import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import WarningAlt16 from '@carbon/icons-react/es/warning--alt/16';
import { useBoolean } from 'ahooks';
import { Dropdown, Tooltip } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId } from 'api/models';
import { TaskType } from 'api/tasks';
import { ReactComponent as MilestoneTodoIcon } from 'assets/svgs/milestone.svg';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { PermissionRuleName } from 'constant';
import { useProjectDetail } from 'data/projects/hooks';
import { Task } from 'data/tasks';
import {
  useCurrentUserProjectAccess,
  useProjectTaskPermission,
  usePermissionForRule,
} from 'hooks/permissions';
import { DuplicateTaskDrawer } from '../../../organisms/tasks/duplicate-task';
import { useErrorSound } from './get-error-sound';
import { MoveTaskAlertModal } from './move-task-alert-modal';
import { MoveTaskSubMenu } from './move-task-sub-menu';
import { useTaskMoreActions } from './use-more-actions';

enum MoreActionsKeyHash {
  MarkAtRisk = 'MARK_AT_RISK',
  ConvertToMilestone = 'CONVERT_TO_MILESTONE',
  Delete = 'DELETE',
  ChangePrivacy = 'CHANGE_PRIVACY',
}

interface Props {
  data: Task;
}

export const TaskMoreActions: FC<Props> = ({ data }) => {
  const { project: projectId } = data;
  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));
  const taskPermission = useProjectTaskPermission(ProjectId(projectId));
  const [
    isDuplicateTaskDrawerVisible,
    { setTrue: openDuplicateTaskDrawer, setFalse: closeDuplicateTaskDrawer },
  ] = useBoolean(false);
  const [playSound] = useErrorSound();
  const isMilestone = data.type === TaskType.Milestone;
  const { isProjectAdmin } = useCurrentUserProjectAccess(
    ProjectId(data.project),
  );
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const canDelete =
    (isMilestone ? isProjectAdmin : taskPermission.canDelete) &&
    isAllowedForCurrentUser(PermissionRuleName.TaskDeleteAction);
  const {
    handleMoveTask,
    handleConvertToMilestone,
    handleMarkAtRisk,
    togglePrivate,
    moveTaskState,
    handleMoveTaskCancel,
    deleteConfirmation,
    handleMoveTaskSubmit,
    isUpdateTaskPhaseLoading,
  } = useTaskMoreActions({ data });

  const { projectPhase } = moveTaskState;

  const phaseDetail =
    projectDetail?.projectPhases?.filter(
      (phase) => phase.projectPhaseId === data.projectPhase?.projectPhaseId,
    ) || [];

  const isPhasePrivate = phaseDetail?.[0]?.private ?? false;
  const isParentPrivate = data.parentTaskObject?.private ?? false;

  // when say a task/subtask is shared but the phase/parent-task is private, we need the option to make it private but not shared.
  const showPrivacyMenuItem =
    !(isPhasePrivate || isParentPrivate) || !data.private;

  const MoreActionsMenu = () => (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      {isProjectAdmin && (
        <Menu.Item
          key={MoreActionsKeyHash.ConvertToMilestone}
          icon={<MilestoneTodoIcon />}
          onClick={handleConvertToMilestone}
          data-cy={`task_drawer.more_actions.convert_to_${
            isMilestone ? 'task' : 'milestone'
          }`}
        >
          {isMilestone ? (
            <FormattedMessage id="task.convert_to_task" />
          ) : (
            <FormattedMessage id="task.convert_to_milestone" />
          )}
        </Menu.Item>
      )}
      <Menu.Item
        key={MoreActionsKeyHash.MarkAtRisk}
        icon={<WarningAlt16 />}
        onClick={handleMarkAtRisk}
        data-cy="task_drawer.more_actions.at_risk"
      >
        {data && data.atRisk ? (
          <FormattedMessage id="task.remove_at_risk" />
        ) : (
          <FormattedMessage id="task.mark_at_risk" />
        )}
      </Menu.Item>
      {isAllowedForCurrentUser(PermissionRuleName.TaskChangePrivacyAction) &&
        showPrivacyMenuItem && (
          <Menu.Item
            key={MoreActionsKeyHash.ChangePrivacy}
            icon={data.private ? <View16 /> : <ViewOff16 />}
            onClick={togglePrivate}
          >
            {data && data.private ? (
              <FormattedMessage id="task.mark_shared" />
            ) : (
              <FormattedMessage id="task.mark_private" />
            )}
          </Menu.Item>
        )}
      {isAllowedForCurrentUser(PermissionRuleName.TaskMoveBetweenPhaseAction) &&
        !data.parentTask && (
          <MoveTaskSubMenu
            phases={projectDetail?.projectPhases?.filter(
              ({ projectPhaseId }) =>
                projectPhaseId !== data.projectPhase?.projectPhaseId,
            )}
            onClick={handleMoveTask}
          />
        )}
      {isAllowedForCurrentUser(PermissionRuleName.TaskDuplicateAction) &&
      !data.formInstanceId ? (
        <>
          {data.parentTask ? (
            <Menu.Item
              data-cy="task.duplicate_subtask"
              icon={<Copy16 />}
              onClick={openDuplicateTaskDrawer}
            >
              <FormattedMessage id="task.duplicate_sub_task" />
            </Menu.Item>
          ) : (
            <Menu.Item
              data-cy="task_more_options.duplicateTask"
              icon={<Copy16 />}
              onClick={openDuplicateTaskDrawer}
            >
              <FormattedMessage id="task.duplicate_task" />
            </Menu.Item>
          )}
        </>
      ) : null}
      {canDelete && (
        <>
          <Menu.Divider />
          <Menu.ItemDelete
            data-cy="task-more-actions__delete"
            key={MoreActionsKeyHash.Delete}
            onClick={() => {
              deleteConfirmation();
              playSound();
            }}
          />
        </>
      )}
    </Menu>
  );

  return (
    <>
      {isDuplicateTaskDrawerVisible ? (
        <DuplicateTaskDrawer
          task={data}
          closeHandler={closeDuplicateTaskDrawer}
          onCancel={closeDuplicateTaskDrawer}
        />
      ) : null}
      <Dropdown
        overlay={MoreActionsMenu}
        trigger={['click']}
        placement="bottomRight"
      >
        <Tooltip title={<FormattedMessage id="task.more_actions" />}>
          <Button
            className="task-more_actions-trigger"
            type="text"
            icon={<OverflowMenuVertical16 />}
            size="small"
            data-cy={`task.more_actions.${data.taskName}`}
          />
        </Tooltip>
      </Dropdown>
      {projectPhase ? (
        <MoveTaskAlertModal
          task={data}
          projectPhase={projectPhase}
          onCancel={handleMoveTaskCancel}
          onSubmit={handleMoveTaskSubmit}
          confirmLoading={isUpdateTaskPhaseLoading}
        />
      ) : null}
    </>
  );
};
